import Table from "@shared/Table";
import dateFormat from "dateformat";
import { useState, useEffect, useRef, useCallback, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {PrinterIcon } from '@heroicons/react/24/outline'
import { useReactToPrint } from "react-to-print";
import useStallholders from "@hooks/useStallholders";
import { formatPrice } from "@shared/Utils";
import { StallholderPayment } from "@sharedtypes/StallholderPayment";
import StallholderSummary from "@components/StallholderSummary";
import { Stallholder } from "@sharedtypes/Stallholder";
import { stripTimeFromDate } from "@shared/Utils";

type PaymentReportProps = {
  payments: StallholderPayment[];
  endDate: Date;
};

const PaymentReport = ({payments, endDate }: PaymentReportProps) => {

  const stallholderContext = useStallholders();
  const [data, setData] = useState<StallholderPayment[]>([]);
  const [total, setTotal] = useState(0);
  const [printableReports, setPrintableReports] = useState<ReactNode[]>([]);

  const iPadPrintFix = `
    @page { margin: 8mm; }
    @media print {
      html, body {
        height: auto !important;
        overflow: visible !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      /* iPad Safari print killers */
      * {
        overflow: visible !important;
        -webkit-overflow-scrolling: auto !important;
        contain: none !important;
        transform: none !important;
        filter: none !important;
        backdrop-filter: none !important;
      }

      /* If you use sticky headers anywhere */
      .sticky, [style*="position:sticky"], [style*="position: fixed"] {
        position: static !important;
      }

      /* Reset off-screen positioning for print */
      [style*="left: -9999px"] {
        position: static !important;
        left: auto !important;
        top: auto !important;
        width: auto !important;
      }

      /* Duplex page-break spacer — needs both legacy and modern properties
         so Safari's WebKit engine respects the break (it prefers break-before:page
         over the older page-break-before:always inline style) */
      .duplex-spacer {
        break-before: page !important;
        page-break-before: always !important;
        display: block !important;
        height: 1px !important;
      }

      /* Compact dealer-report tables so each transaction fits on one line.
         Targets only dealer reports; the payment summary is unaffected. */
      .dealer-report table {
        font-size: 9px !important;
        line-height: 1.3 !important;
      }

      /* Zero the inner wrapper margins so tables align flush with section
         headings ("Debit", "Credit", etc.). The print:m-0 Tailwind classes
         on these divs lose the cascade battle against m-2 because Tailwind
         generates m-2 later in the stylesheet; !important here wins. */
      .dealer-report .overflow-scroll {
        margin: 0 !important;
      }

    }
  `;

  const dealerReports = useRef<HTMLDivElement>(null);

  const triggerPrint = useReactToPrint({
    contentRef: dealerReports,
    preserveAfterPrint: true,
    pageStyle: iPadPrintFix,
    onAfterPrint: () => {
      dealerReports.current?.querySelectorAll('.duplex-spacer, .summary-spacer').forEach(el => el.remove());
    }
  });

  const printDealerReports = useCallback(() => {
    const container = dealerReports.current;
    if (!container) return;

    // Remove any previous spacers
    container.querySelectorAll('.duplex-spacer, .summary-spacer').forEach(el => el.remove());

    // Temporarily swap @media print → @media all so print styles
    // (print:text-xs, print:p-0, etc.) apply during measurement
    const mediaChanges: CSSMediaRule[] = [];
    for (const sheet of document.styleSheets) {
      try {
        for (const rule of sheet.cssRules) {
          if (rule instanceof CSSMediaRule && rule.media.mediaText === 'print') {
            mediaChanges.push(rule);
            rule.media.deleteMedium('print');
            rule.media.appendMedium('all');
          }
        }
      } catch (e) { /* cross-origin stylesheets */ }
    }

    // Set container to print content width (A4 210mm - 2×8mm margins)
    const prevWidth = container.style.width;
    container.style.width = '194mm';

    // Force layout recalculation
    void container.offsetHeight;

    // Measure A4 content height (297mm - 2×8mm margins = 281mm) in pixels
    const ruler = document.createElement('div');
    ruler.style.cssText = 'width:281mm;position:absolute;visibility:hidden';
    document.body.appendChild(ruler);
    const pageHeightPx = ruler.offsetWidth;
    document.body.removeChild(ruler);

    const reports = Array.from(container.querySelectorAll(':scope > .dealer-report'));
    let runningPages = 0;
    // Tracks the actual print-space pixel offset at which the current report
    // starts.  Using runningPages * pageHeightPx would overcount because
    // Math.ceil rounds each report up to a full page even when reports flow
    // continuously without a spacer between them.
    let reportPrintStart = 0;


    for (let i = 0; i < reports.length; i++) {
      const report = reports[i] as HTMLElement;

      // If the summary block would straddle a page boundary, insert a
      // break-before:page spacer before it — same pattern as duplex spacers.
      // getBoundingClientRect() is used instead of offsetTop because the
      // summary is nested several levels deep with no guaranteed offset parent.
      const summaryBlock = report.querySelector('.summary-block') as HTMLElement | null;
      if (summaryBlock) {
        const reportTop = report.getBoundingClientRect().top;
        const summaryRect = summaryBlock.getBoundingClientRect();
        const summaryTopPx  = reportPrintStart + (summaryRect.top  - reportTop);
        const summaryEndPx  = summaryTopPx + summaryRect.height;

        if (Math.floor(summaryTopPx / pageHeightPx) !== Math.floor(summaryEndPx / pageHeightPx)) {
          const spacer = document.createElement('div');
          spacer.className = 'summary-spacer';
          spacer.style.breakBefore = 'page';
          spacer.style.pageBreakBefore = 'always';
          spacer.style.height = '1px';
          spacer.style.display = 'block';
          spacer.innerHTML = '&nbsp;';
          summaryBlock.insertAdjacentElement('beforebegin', spacer);
        }
      }

      const pages = Math.ceil(report.offsetHeight / pageHeightPx);

      runningPages += pages;

      if (runningPages % 2 === 1 && i < reports.length - 1) {
        const spacer = document.createElement('div');
        spacer.className = 'duplex-spacer';
        spacer.style.breakBefore = 'page';        // modern CSS fragmentation (Safari)
        spacer.style.pageBreakBefore = 'always';  // legacy fallback (Chrome / older WebKit)
        spacer.style.height = '1px';
        spacer.style.display = 'block';
        spacer.innerHTML = '&nbsp;';
        report.insertAdjacentElement('afterend', spacer);
        runningPages += 1;
        // The duplex spacer forces a page break, so the next report starts at
        // the next full page boundary after this report's content ends.
        reportPrintStart = Math.ceil((reportPrintStart + report.offsetHeight) / pageHeightPx) * pageHeightPx;
      } else {
        // No spacer: next report flows directly after this one.
        reportPrintStart += report.offsetHeight;
      }
    }

    // Restore container width
    container.style.width = prevWidth;

    // Restore @media print rules
    for (const rule of mediaChanges) {
      rule.media.deleteMedium('all');
      rule.media.appendMedium('print');
    }

    triggerPrint();
  }, [triggerPrint]);

  const printable = useRef<HTMLInputElement>(null);
  const printSummaryReports = useReactToPrint({ contentRef: printable, preserveAfterPrint: true, pageStyle: iPadPrintFix });


  useEffect(() => {
    
    const enrichedPayments = payments.slice(0);
    const reports: ReactNode[] = [];
    const end = stripTimeFromDate(endDate);
    var newTotal = 0;

    enrichedPayments.forEach(function (item, index) {
      const stallholder = stallholderContext.findStallholderByID(item.id);
      if (stallholder) {
        newTotal += item.amount;
        item.name = stallholder.name;
        item.dealerID = stallholder.dealer_id;
        reports.push(<DealerReport key={item.id} startDate={item.start} endDate={end} stallholder={stallholder}/>)
      }

    });
    setTotal(newTotal);
    setData(enrichedPayments);
    setPrintableReports(reports);

    return () => {
    };
  }, [payments]); 

  const columns = [
    {
      "id": "dealerID",
      "label": "Dealer ID",
      "cellStyle": (row: number) => "px-6 py-1 font-medium text-lg text-gray-900 whitespace-nowrap dark:text-white dark:bg-gray-800",
      "headerStyle": "px-6 py-3 underline"
    },
    {
      "id": "name",
      "label": "Name",
      "cellStyle": (row: number) => "px-6 py-1 text-lg",
      "headerStyle": "px-6 py-4 underline"
    },
    {
      "id": "amount",
      "label": "Payment",
      "cellStyle": (row: number) => "px-6 py-1 text-lg",
      "headerStyle": "px-6 py-4 underline",
      "format": (cell: any, row: {}, index: number) => { 
        const price = cell as number;
        return formatPrice(price);
      }
    },
    {
      "id": "start",
      "label": "After",
      "cellStyle": (row: number) => "px-6 py-1 text-lg",
      "headerStyle": "px-6 py-4 underline"
    }
  ]

  const footer = <tfoot>
      <tr className="font-semibold text-gray-900 dark:text-white">
        <th></th>
        <th></th>
        <th scope="row" className="ml-10 border-t-2 border-b-2 border-zinc-600 px-6 text-base">{formatPrice(total)}</th>
      </tr>
    </tfoot>

  return (
    <>
      <div className="flex justify-end mb-12">

      <div>
        <button onClick={() => printDealerReports()}
                className="mr-6 inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <PrinterIcon className="w-6 h-6"/>
          Print dealer reports
        </button>
      </div>

      <div>
        <button onClick={() => printSummaryReports()}className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <PrinterIcon className="w-6 h-6"/>
          Print payments summary
        </button>
      </div>

      </div>
      <div>
        <h1 className="font-bold text-2xl flex justify-start mb-6">Payment report for close of business {dateFormat(endDate, "dddd, mmmm dS")}</h1>
        <Table data={data} columns={columns} footer={footer}/>
      </div>

      {createPortal(
        <div ref={printable}
             style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '210mm' }}>
          <h1 className="font-bold text-2xl flex justify-start mb-6">Payment report for close of business {dateFormat(endDate, "dddd, mmmm dS")}</h1>
          <Table data={data} columns={columns} footer={footer}/>
        </div>,
        document.body
      )}

      {createPortal(
        <div ref={dealerReports}
             style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '210mm' }}>
          {printableReports}
        </div>,
        document.body
      )}
    </>
  );
}

type DealerReportProps = {
  stallholder: Stallholder;
  endDate: string;
  startDate: string;
};

const DealerReport = ({stallholder, endDate, startDate }: DealerReportProps) => {

  return(
    <div className="dealer-report">
      <h1 className="text-2xl font-bold">Transaction summary from {dateFormat(startDate, "dS mmmm")} to {dateFormat(endDate, "dS mmmm yyyy")}</h1>
      <StallholderSummary start={stallholder.lastPaymentDateCalculation} end={endDate} stallholder={stallholder} showPrintButton={false} />
    </div>
  );
}

export default PaymentReport;
